import type { WeeklyAvailability, AthleteProfile, TrainingGoal, ProfileRelation, Profile, PlanState, EnabledSports, TriathlonProgression } from '@/types';
import { DEFAULT_GAMIFICATION_STATE } from '../gamification/types';
import type { UnitSystem } from '../units';
import { DEFAULT_WEEKLY_CHECKIN_PREFERENCES } from '@/types/weekly-checkin';

// ============================================================================
// FEATURE FLAGS
// ============================================================================

/**
 * Default enabled sports.
 * Running and Cycling are enabled by default.
 * Swimming and Triathlon require explicit enablement (for staged rollout).
 */
export const defaultEnabledSports: EnabledSports = {
    running: true,
    cycling: true,
    swimming: false,
    triathlon: false,
    strava: false,
};

// ============================================================================
// TRIATHLON PROGRESSION
// ============================================================================

/**
 * Default triathlon progression settings.
 * Used when athlete.sport === 'Triathlon'.
 */
export const defaultTriathlonProgression: TriathlonProgression = {
    swimCTL: 0,
    bikeCTL: 0,
    runCTL: 0,
    compositeCTL: 0,
    swimmingMetrics: {
        css: 120,           // 2:00 per 100m default CSS
        weeklySwimTSS: 100,
        swimFrequency: 2,
    },
    sessionCaps: {
        swim: null,
        bike: null,
        run: null,
        strength: null,
    },
    disciplineDistribution: {
        swim: 20,
        bike: 50,
        run: 25,
        strength: 5,
    },
    weeklyRhythm: {
        frequencyTargets: {
            swim: null,
            bike: null,
            run: null,
            strength: null,
        },
        preferredDays: {
            swim: [1, 3],
            bike: [2, 4, 6],
            run: [1, 3, 5],
            strength: [2, 4],
        },
        anchorDays: {
            longBike: [6],
            longRun: [0],
        },
        fixedSessions: {
            swimLessons: [],
        },
        flexibilityMode: 'balanced',
    },
    targetDistance: 'olympic',
    rampRate: 6,
    progressionSettings: {
        running: {
            mode: 'distance',
            progressionLevel: 'regular',
            baselineDistanceKm: 18,
        },
        cycling: {
            mode: 'load',
            progressionLevel: 'regular',
            baselineLoad: 180,
            baselineHours: 3,
        },
        swimming: {
            mode: 'time',
            progressionLevel: 'regular',
            baselineMinutes: 90,
        },
    },
};

export function cloneTriathlonProgression(
    progression: TriathlonProgression = defaultTriathlonProgression
): TriathlonProgression {
    const defaultSessionCaps = defaultTriathlonProgression.sessionCaps!;
    return {
        ...defaultTriathlonProgression,
        ...progression,
        swimmingMetrics: {
            ...defaultTriathlonProgression.swimmingMetrics,
            ...progression.swimmingMetrics,
        },
        sessionCaps: {
            swim: defaultSessionCaps.swim,
            bike: defaultSessionCaps.bike,
            run: defaultSessionCaps.run,
            strength: defaultSessionCaps.strength,
            ...progression.sessionCaps,
        },
        disciplineDistribution: {
            ...defaultTriathlonProgression.disciplineDistribution,
            ...progression.disciplineDistribution,
        },
        disciplineFocus: progression.disciplineFocus
            ? { ...progression.disciplineFocus }
            : defaultTriathlonProgression.disciplineFocus,
        weeklyRhythm: progression.weeklyRhythm
            ? {
                ...defaultTriathlonProgression.weeklyRhythm!,
                ...progression.weeklyRhythm,
                frequencyTargets: {
                    ...defaultTriathlonProgression.weeklyRhythm!.frequencyTargets,
                    ...progression.weeklyRhythm.frequencyTargets,
                },
                preferredDays: {
                    swim: [...(progression.weeklyRhythm.preferredDays?.swim ?? defaultTriathlonProgression.weeklyRhythm!.preferredDays.swim)],
                    bike: [...(progression.weeklyRhythm.preferredDays?.bike ?? defaultTriathlonProgression.weeklyRhythm!.preferredDays.bike)],
                    run: [...(progression.weeklyRhythm.preferredDays?.run ?? defaultTriathlonProgression.weeklyRhythm!.preferredDays.run)],
                    strength: [...(progression.weeklyRhythm.preferredDays?.strength ?? defaultTriathlonProgression.weeklyRhythm!.preferredDays.strength)],
                },
                anchorDays: {
                    longBike: [...(progression.weeklyRhythm.anchorDays?.longBike ?? defaultTriathlonProgression.weeklyRhythm!.anchorDays.longBike)],
                    longRun: [...(progression.weeklyRhythm.anchorDays?.longRun ?? defaultTriathlonProgression.weeklyRhythm!.anchorDays.longRun)],
                },
                fixedSessions: {
                    swimLessons: progression.weeklyRhythm.fixedSessions?.swimLessons
                        ? progression.weeklyRhythm.fixedSessions.swimLessons.map((lesson) => ({ ...lesson }))
                        : [],
                },
            }
            : undefined,
        progressionSettings: progression.progressionSettings
            ? {
                totalBaselineHours: progression.progressionSettings.totalBaselineHours,
                running: {
                    ...defaultTriathlonProgression.progressionSettings!.running,
                    ...progression.progressionSettings.running,
                },
                cycling: {
                    ...defaultTriathlonProgression.progressionSettings!.cycling,
                    ...progression.progressionSettings.cycling,
                },
                swimming: {
                    ...defaultTriathlonProgression.progressionSettings!.swimming,
                    ...progression.progressionSettings.swimming,
                },
            }
            : defaultTriathlonProgression.progressionSettings,
        weeklyTargets: progression.weeklyTargets
            ? {
                ...progression.weeklyTargets,
                swimming: { ...progression.weeklyTargets.swimming },
                cycling: { ...progression.weeklyTargets.cycling },
                running: { ...progression.weeklyTargets.running },
                strength: { ...progression.weeklyTargets.strength },
            }
            : undefined,
    };
}

export const defaultAvailability: WeeklyAvailability = {
    0: { hours: 2.0, split: false, amHours: 1.0, pmHours: 1.0 },
    1: { hours: 1.5, split: false, amHours: 0.75, pmHours: 0.75 },
    2: { hours: 1.5, split: false, amHours: 0.75, pmHours: 0.75 },
    3: { hours: 1.5, split: false, amHours: 0.75, pmHours: 0.75 },
    4: { hours: 1.5, split: false, amHours: 0.75, pmHours: 0.75 },
    5: { hours: 1.5, split: false, amHours: 0.75, pmHours: 0.75 },
    6: { hours: 2.5, split: false, amHours: 1.25, pmHours: 1.25 },
};

/**
 * P0 Audit Fix: Safe defaults for Foundation / Everyday Athletes.
 * 20 min × 3 days/week = 1h total. Prevents injury from over-ambitious defaults.
 */
export const foundationAvailability: WeeklyAvailability = {
    0: { hours: 0, split: false, amHours: 0, pmHours: 0 },       // Sun - rest
    1: { hours: 0, split: false, amHours: 0, pmHours: 0 },       // Mon - rest
    2: { hours: 0.5, split: false, amHours: 0.5, pmHours: 0 },   // Tue - 30min
    3: { hours: 0, split: false, amHours: 0, pmHours: 0 },       // Wed - rest
    4: { hours: 0.5, split: false, amHours: 0.5, pmHours: 0 },   // Thu - 30min
    5: { hours: 0, split: false, amHours: 0, pmHours: 0 },       // Fri - rest
    6: { hours: 0.5, split: false, amHours: 0.5, pmHours: 0 },   // Sat - 30min
};

export function deepCloneAvailability(avail: WeeklyAvailability): WeeklyAvailability {
    return Object.fromEntries(
        Object.entries(avail).map(([k, v]) => [k, { ...v }])
    );
}

export const defaultProgression = {
    startingVolume: 30,
    progressionRate: 0.10,
};

export const defaultCyclingProgression = {
    startingTSS: 350,
    ftp: 200,
    rampRate: 6,
    baselineCTL: 20,
    ctlSource: 'estimated' as 'intervals_icu' | 'manual' | 'estimated',
    averageWeeklyHours: 6,
};

export const defaultPlanSettings = {
    progressionRate: 0.1,
    taperDuration: 2,
    startWithRestWeek: false,
    volumeMultiplier: 1.0,
    hrvReadinessSuggestionsEnabled: false,
    strictIntensityDistribution: false,
    enableUnsafePlanning: false,
    useDurationTargeting: true, // Enabled by default for the new UX
    maxBigRocksPerWeek: 3,      // Default to 3 key sessions
    minRunDuration: 30,         // Default to 30 mins
};

export const defaultAthlete: AthleteProfile = {
    name: '',
    sport: 'Running' as const,
    experience: 'consistent' as const,
    gymAccess: 'no_gym' as const,
    zones: {
        running: { power: [], heartRate: [], pace: [] },
        cycling: { power: [], heartRate: [], pace: [] },
        swimming: { power: [], heartRate: [], pace: [], swimPace: [] }
    },
    maxStrengthSessionsPerWeek: 2,
    preferredWorkoutsPerWeek: undefined,
};

export const defaultGoal: TrainingGoal = {
    type: 'event' as const,
    raceDate: '',
};

export function createEmptyProfile(
    id: string,
    intervalsAthleteId: string,
    displayName: string,
    relation: ProfileRelation,
    sport: Profile['athlete']['sport'] = 'Running'
): Profile {
    const now = new Date().toISOString();
    return {
        id,
        intervalsAthleteId,
        relation,
        displayName,
        createdAt: now,
        lastAccessedAt: now,
        isHydrated: false,

        // Auth Defaults
        role: 'athlete',
        isPro: false,
        disabled: false,

        athlete: { ...defaultAthlete, name: displayName, sport },
        metrics: {},
        goal: { ...defaultGoal },
        events: [],
        availability: deepCloneAvailability(defaultAvailability),
        longRunDay: 6,
        plan: [],
        planState: 'active' as PlanState,
        planName: undefined,
        planHistory: [],
        blocks: [],
        config: null,
        progression: { ...defaultProgression },
        cyclingProgression: { ...defaultCyclingProgression },
        planSettings: { ...defaultPlanSettings },
        triathlonProgression: cloneTriathlonProgression(),
    };
}

/**
 * Extract profile-specific data for switching active profile.
 * IMPORTANT: This should ONLY return profile-scoped data.
 * User-level settings (preferences, strava, enabledSports, etc.)
 * must NOT be included — they are preserved across profile switches.
 */
export function extractProfileData(profile: Profile) {
    const safeGoal = profile.goal ? { ...defaultGoal, ...profile.goal } : { ...defaultGoal };
    const safeProgression = profile.progression ? { ...defaultProgression, ...profile.progression } : { ...defaultProgression };
    const safePlanSettings = profile.planSettings ? { ...defaultPlanSettings, ...profile.planSettings } : { ...defaultPlanSettings };
    const safeAvailability = profile.availability ? deepCloneAvailability(profile.availability) : deepCloneAvailability(defaultAvailability);
    const safeEvents = Array.isArray(profile.events) ? profile.events : [];
    const safePlan = Array.isArray(profile.plan) ? profile.plan : [];
    const safePlanHistory = Array.isArray(profile.planHistory) ? profile.planHistory : [];
    const safeBlocks = Array.isArray(profile.blocks) ? profile.blocks : [];

    const syncedFeatureFlags = profile.planSettings?._syncedData?.featureFlags;
    const hasSyncedFeatureFlags = Boolean(
        syncedFeatureFlags
        && typeof syncedFeatureFlags === 'object'
        && Object.keys(syncedFeatureFlags).length > 0
    );

    return {
        athlete: { ...defaultAthlete, ...profile.athlete },
        metrics: profile.metrics,
        goal: safeGoal,
        events: safeEvents,
        availability: safeAvailability,
        longRunDay: profile.longRunDay,
        plan: safePlan,
        planState: profile.planState,
        planName: profile.planName,
        planHistory: safePlanHistory,
        blocks: safeBlocks,
        config: profile.config,
        progression: safeProgression,
        cyclingProgression: { ...defaultCyclingProgression, ...profile.cyclingProgression },
        planSettings: safePlanSettings,
        triathlonProgression: cloneTriathlonProgression(profile.triathlonProgression || defaultTriathlonProgression),
        // Note: athleteId changes per profile
        intervals: {
            athleteId: profile.intervalsAthleteId,
        },
        // Preserve the active global feature-flag state unless this profile
        // explicitly carries a synced backup payload.
        ...(hasSyncedFeatureFlags ? { featureFlags: syncedFeatureFlags } : {}),
        // Do NOT reset strava — it's user-level, not profile-level
    };
}

export const initialState = {
    userRole: 'athlete' as const,
    profiles: [],
    activeProfileId: null,
    isSupportImpersonation: false,
    athlete: { ...defaultAthlete },
    metrics: {},
    goal: { ...defaultGoal },
    events: [],
    intervals: { athleteId: '' },
    strava: {},
    stravaConfig: {
        clientId: import.meta.env?.VITE_STRAVA_CLIENT_ID ?? '',
        clientSecret: import.meta.env?.VITE_STRAVA_CLIENT_SECRET ?? ''
    },
    availability: deepCloneAvailability(defaultAvailability),
    longRunDay: 6,
    plan: [],
    planState: 'active' as PlanState,
    planName: undefined,
    planHistory: [],
    blocks: [],
    config: null,
    modifications: {},
    progression: { ...defaultProgression },
    cyclingProgression: { ...defaultCyclingProgression },
    planSettings: { ...defaultPlanSettings },
    preferences: {
        proMode: false,
        darkMode: false,
        unitSystem: 'metric' as UnitSystem,
        weekStart: 'Monday' as const,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
        dateFormat: 'DD/MM/YYYY' as const,
        timeFormat: '24h' as const,
        weeklyCheckIn: { ...DEFAULT_WEEKLY_CHECKIN_PREFERENCES },
        hasSeenWalkthrough: false,
        showEducation: true,
        unplannedClickExternal: true,
    },
    viewMode: 'scroll' as const,
    selectedWeek: 1,
    dragState: null,
    isLoading: false,
    isHydrating: false,
    lifeStatus: 'fresh' as const,
    stressLevel: 0,
    gamification: { ...DEFAULT_GAMIFICATION_STATE },
    // Feature Flags
    featureFlags: {},
    enabledSports: { ...defaultEnabledSports },
    paidFeatures: {
        intervalsSync: false,
        workoutUpload: false,
        planPrinting: false,
    },
    enforcePremium: true,
    // Triathlon
    triathlonProgression: cloneTriathlonProgression(),
    // Discovery Hub
    discovery: {
        active: false,
        persona: 'normal' as const,
        discoveredSteps: [] as string[],
        rewardType: null as 'sync' | 'print' | null,
        currentStepIdx: 0,
    },
    // Conversion Tracking
    stats: {
        workoutsWatched: 0,
        screensVisited: 0,
    },
    consent: {
        essential: true,
        analytics: false,
        health: false,
        dismissed: false,
    },
    pendingSync: {
        active: false,
        source: '',
        changes: [] as { field: string; label: string; oldSummary: string; newSummary: string; severity: 'info' | 'warning' | 'destructive' }[],
        applyFn: null as (() => void) | null,
    },
};
